import moment from 'moment';
import React from 'react';

import Link from '@/components/Link';
import { money } from '@/core/money';

import { RANGE_PRESETS, type RangePreset, type ResolvedRange } from './useDateRange';
import { useRovingRadioGroup } from './useRovingRadioGroup';

// Account Overview header (ENG-5256): title + billing link, the big balance
// figure (credits remaining for credit teams, the monthly per-seat price for
// SMB) with a baseline-aligned date-range control, and a "{Company} · {Plan}"
// line beside a human range-confirmation phrase.
//
// The date-range control is a SECOND radiogroup driven by the same
// useRovingRadioGroup roving model (NOT the role=tablist SegmentedControl), so
// the four presets are arrow-navigable, wrap, and respond to Home/End.

interface AccountRangeHeaderProps {
  companyName: string;
  /** Display plan label (e.g. "Enterprise Plan"); null hides the segment. */
  planLabel: string | null;
  /** Credits remaining (whole credits). Null when billing is unavailable (credit loading). */
  creditsRemaining: number | null;
  /**
   * SMB subscription price per seat, in cents, on the subscription's billing
   * interval. Null when unavailable (loading) — the SMB balance is then hidden.
   * Mirrors Settings: the headline shows this normalized to a monthly per-seat
   * price, never a multi-seat total.
   */
  planPerSeatCents: number | null;
  /** Currency of planPerSeatCents (e.g. "USD"); pairs with it for money() formatting. */
  planPerSeatCurrency: string | null;
  /** SMB seat count, appended to Row-2 as " · {n} seat(s)" beside the plan label. */
  planQuantity: number | null;
  /** Subscription billing interval enum (MONTH/YEAR); YEAR normalizes the per-seat price /12. Drives Row-2's "Billed annually/monthly" segment. */
  billingInterval: string | null;
  /** SMB swaps "Manage Billing →" for "Team Settings →" and the credits figure for the per-seat price. */
  isSMB: boolean;
  settingsUrl: string;
  range: ResolvedRange;
  onPresetChange: (preset: RangePreset) => void;
}

/**
 * Human confirmation of the active window. A concrete preset reads as
 * "Jun 2025 – Jun 2026"; "All" collapses to "All time".
 */
export function rangeConfirmationPhrase(range: ResolvedRange): string {
  if (range.preset === 'all' || !range.start_date) return 'All time';
  const start = moment(range.start_date).format('MMM YYYY');
  const end = moment(range.end_date).format('MMM YYYY');
  return `${start} – ${end}`;
}

// Row-2 billing-cadence labels by subscription interval (ENG-5299). The
// cadence reads beside the plan name/seat count, not under the price, where
// "$X/month" above "billed annually" was misread as an annual price.
const CADENCE_LABELS: Record<string, string> = {
  YEAR: 'Billed annually',
  MONTH: 'Billed monthly',
};

export function AccountRangeHeader({
  companyName,
  planLabel,
  creditsRemaining,
  planPerSeatCents,
  planPerSeatCurrency,
  planQuantity,
  billingInterval,
  isSMB,
  settingsUrl,
  range,
  onPresetChange,
}: AccountRangeHeaderProps): React.ReactNode {
  const linkText = isSMB ? 'Team Settings' : 'Manage Billing';

  const { getOptionProps } = useRovingRadioGroup({
    ids: RANGE_PRESETS.map((p) => p.value),
    selectedId: range.preset,
    onSelect: (id) => onPresetChange(id as RangePreset),
  });

  // SMB balance mirrors Settings (ManageSeatsModal): show the per-seat price
  // normalized to a monthly figure — never a multi-seat total and never the
  // seat count (that lives on Row-2). Annual subscriptions divide the per-seat
  // price by 12.
  const isAnnual = billingInterval === 'YEAR';
  const showSMBBalance = isSMB && planPerSeatCents !== null;
  const perSeatMonthly = showSMBBalance
    ? money({
        cents: isAnnual ? Math.round(planPerSeatCents / 12) : planPerSeatCents,
        currency: planPerSeatCurrency ?? 'USD',
      }).formatted({ omitEmptyCents: true })
    : null;
  // SMB Row-2 appends "· {n} seat(s)" to the right of the plan label. Hidden
  // for credit/enterprise accounts and when the seat count is unavailable.
  const seatSegment =
    isSMB && planQuantity !== null
      ? `${planQuantity} ${planQuantity === 1 ? 'seat' : 'seats'}`
      : null;
  // SMB Row-2 appends "· Billed annually/monthly" after the seat count.
  // Hidden for credit/enterprise accounts and while the interval is unknown.
  const cadenceSegment =
    isSMB && billingInterval !== null ? (CADENCE_LABELS[billingInterval] ?? null) : null;

  return (
    <div className="flex flex-col gap-12">
      <div className="flex items-center justify-between">
        <h2 className="text-18 font-semibold leading-snug text-on-surface">Account Overview</h2>
        <Link to={settingsUrl} className="text-12 font-medium text-brand-primary">
          {linkText} &rarr;
        </Link>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-12">
        {isSMB
          ? showSMBBalance && (
              <div className="flex items-baseline gap-2">
                <span className="text-36 font-bold tabular-nums leading-none tracking-tight text-on-surface">
                  {perSeatMonthly}
                </span>
                <span className="text-14 text-on-surface-muted">/month per seat</span>
              </div>
            )
          : creditsRemaining !== null && (
              <div className="flex items-baseline gap-6">
                <span className="text-36 font-bold tabular-nums leading-none tracking-tight text-on-surface">
                  {creditsRemaining}
                </span>
                <span className="text-14 text-on-surface-muted">credits remaining</span>
              </div>
            )}

        <div
          role="radiogroup"
          aria-label="Date range"
          className="flex items-center gap-4 rounded-md border border-surface-border bg-surface-secondary p-2"
        >
          {RANGE_PRESETS.map((preset) => {
            const props = getOptionProps(preset.value);
            const isSelected = preset.value === range.preset;
            return (
              <button
                key={preset.value}
                type="button"
                {...props}
                className={[
                  'rounded px-10 py-4 text-12 font-medium transition-colors duration-150',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary-main',
                  isSelected
                    ? 'bg-surface-card text-brand-primary ring-2 ring-inset ring-brand-primary'
                    : 'text-on-surface-muted hover:bg-surface-muted',
                ].join(' ')}
              >
                {preset.short}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-8 text-12 text-on-surface-muted">
        <span>
          {companyName}
          {planLabel && <span>{` · ${planLabel}`}</span>}
          {seatSegment && <span>{` · ${seatSegment}`}</span>}
          {cadenceSegment && <span>{` · ${cadenceSegment}`}</span>}
        </span>
        <span>{rangeConfirmationPhrase(range)}</span>
      </div>
    </div>
  );
}
